<template>
  <div class="origin-form-wrap" :class="{ 'origin-form-wrap--join': joinTop }">
    <table class="origin-form" :style="tableStyle">
      <colgroup>
        <col v-for="(w, n) in colWidths" :key="'col-'+n" :style="{ width: w }" />
      </colgroup>
      <tbody>
        <tr v-for="(row, ri) in rows" :key="'r-'+ri">
          <template v-for="(cell, ci) in visibleCells(ri, row)">
            <td
              v-if="cell.type === 'label'"
              :key="'c-'+ri+'-'+ci"
              :colspan="cellColspan(cell)"
              :rowspan="cellRowspan(cell)"
              class="origin-cell origin-cell--label"
              :class="{ 'origin-cell--vertical': cell.vertical }"
            >{{ cell.t }}</td>
            <td
              v-else-if="cell.type === 'data'"
              :key="'c-'+ri+'-'+ci"
              :colspan="cellColspan(cell)"
              :rowspan="cellRowspan(cell)"
              class="origin-cell origin-cell--data"
            >
              <span v-if="cell.t" class="origin-inline-label">{{ cell.t }}</span>
              <template v-if="cell.fk && !isReadonly(cell)">
                <el-date-picker
                  v-if="cell.widget === 'date'"
                  v-model="localModel[resolveKey(cell.fk)]"
                  type="date"
                  value-format="yyyy-MM-dd"
                  size="mini"
                  class="origin-input"
                  style="width:100%"
                />
                <el-select
                  v-else-if="cell.widget === 'instrument'"
                  v-model="localModel[resolveKey(cell.fk)]"
                  size="mini"
                  class="origin-input origin-select"
                  filterable
                  allow-create
                  default-first-option
                  placeholder="选择或输入仪器"
                  style="width:100%"
                  @change="(v) => onInstrumentPick(v, cell)"
                >
                  <el-option
                    v-for="inst in instrumentsForCell(cell)"
                    :key="inst.code + '-' + inst.name"
                    :label="inst.name"
                    :value="inst.name"
                  />
                </el-select>
                <span v-else-if="cell.widget === 'trace'" class="origin-trace-wrap">
                  <el-checkbox v-model="traceJd" class="origin-trace-cb" @change="syncTraceMethod">检定</el-checkbox>
                  <el-checkbox v-model="traceJz" class="origin-trace-cb" @change="syncTraceMethod">校准</el-checkbox>
                  <el-select
                    v-model="localModel[resolveKey(cell.fk)]"
                    size="mini"
                    class="origin-input origin-select origin-select--inline"
                    filterable
                    allow-create
                    default-first-option
                    placeholder="其他"
                    @change="onTraceMethodInput"
                  >
                    <el-option label="检定" value="检定" />
                    <el-option label="校准" value="校准" />
                    <el-option label="检定/校准" value="检定/校准" />
                  </el-select>
                </span>
                <el-input
                  v-else-if="cell.widget === 'textarea'"
                  v-model="localModel[resolveKey(cell.fk)]"
                  size="mini"
                  class="origin-input"
                  type="textarea"
                  :rows="2"
                />
                <el-input
                  v-else
                  v-model="localModel[resolveKey(cell.fk)]"
                  size="mini"
                  class="origin-input"
                  :placeholder="cell.t || ''"
                />
              </template>
              <span v-else-if="cell.fk && isReadonly(cell)" class="origin-readonly">{{ displayVal(cell) }}</span>
            </td>
          </template>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import {
  resolveFormFk,
  instrumentSiblingKeys,
  instrumentCategoryForKey
} from '@/utils/lab_form_engine'
import labInstruments from '../../../config/lab_instruments.json'

export default {
  name: 'PaperFormTable',
  props: {
    rows: { type: Array, default: () => [] },
    model: { type: Object, required: true },
    totalCols: { type: Number, default: 18 },
    formNum: { type: String, default: '' },
    fkMap: { type: Object, default: null },
    readonlyKeys: { type: Array, default: () => [] },
    ypbh: { type: String, default: '' },
    xmbh: { type: String, default: '' },
    joinTop: { type: Boolean, default: false },
    colWeights: { type: Array, default: null }
  },
  data() {
    return {
      spanSkip: [],
      instrumentCategories: (labInstruments && labInstruments.categories) || [],
      traceJd: false,
      traceJz: false,
      syncingTrace: false,
      traceFieldKey: ''
    }
  },
  computed: {
    localModel() {
      return this.model
    },
    colWidths() {
      if (this.colWeights && this.colWeights.length === this.totalCols) {
        const sum = this.colWeights.reduce((a, b) => a + b, 0)
        return this.colWeights.map(w => ((w / sum) * 100).toFixed(4) + '%')
      }
      const w = (100 / this.totalCols).toFixed(4) + '%'
      return Array(this.totalCols).fill(w)
    },
    tableStyle() {
      return { '--origin-cols': this.totalCols }
    },
    activeTraceKey() {
      return this.traceFieldKey
    }
  },
  watch: {
    rows: {
      immediate: true,
      handler() {
        this.rebuildSpanSkip()
        this.detectTraceField()
      }
    },
    activeTraceKey: {
      immediate: true,
      handler(key) {
        if (!key || this.syncingTrace) return
        this.applyTraceMethodFromValue(this.model[key])
      }
    }
  },
  methods: {
    resolveKey(fk) {
      if (this.fkMap && this.fkMap[fk]) return this.fkMap[fk]
      return resolveFormFk(null, fk, this.formNum)
    },
    detectTraceField() {
      let found = ''
      for (const row of this.rows) {
        for (const cell of row) {
          if (cell.widget === 'trace' && cell.fk) {
            found = this.resolveKey(cell.fk)
            break
          }
        }
        if (found) break
      }
      this.traceFieldKey = found
    },
    instrumentsForCell(cell) {
      const key = this.resolveKey(cell.fk)
      const catId = instrumentCategoryForKey(key, this.formNum)
      const cat = this.instrumentCategories.find(c => c.id === catId)
      if (cat && cat.instruments && cat.instruments.length) return cat.instruments
      return this.instrumentCategories.reduce((acc, c) => acc.concat(c.instruments || []), [])
    },
    cellColspan(cell) {
      return cell.cs || cell.colspan || 1
    },
    cellRowspan(cell) {
      return cell.rs || cell.rowspan || 1
    },
    rebuildSpanSkip() {
      const skip = []
      this.rows.forEach((row, ri) => {
        if (!skip[ri]) skip[ri] = []
        let col = 0
        row.forEach(cell => {
          while (skip[ri][col]) col++
          const cs = this.cellColspan(cell)
          const rs = this.cellRowspan(cell)
          for (let r = 0; r < rs; r++) {
            for (let c = 0; c < cs; c++) {
              const tr = ri + r
              const tc = col + c
              if (!skip[tr]) skip[tr] = []
              if (r === 0 && c === 0) continue
              skip[tr][tc] = true
            }
          }
          col += cs
        })
      })
      this.spanSkip = skip
    },
    visibleCells(ri, row) {
      const skip = this.spanSkip[ri] || []
      const out = []
      let col = 0
      row.forEach(cell => {
        while (skip[col]) col++
        out.push(cell)
        col += this.cellColspan(cell)
      })
      return out
    },
    isReadonly(cell) {
      if (cell.ro) return true
      const k = this.resolveKey(cell.fk)
      return k === '__ypbh__' || k === '__xmbh__' || this.readonlyKeys.indexOf(k) >= 0
    },
    displayVal(cell) {
      const k = this.resolveKey(cell.fk)
      if (k === '__ypbh__') return this.ypbh || '—'
      if (k === '__xmbh__') return this.xmbh || '—'
      const v = this.model[k]
      return v !== null && v !== undefined && v !== '' ? v : '—'
    },
    findInstrument(name) {
      if (!name) return null
      for (const cat of this.instrumentCategories) {
        const hit = (cat.instruments || []).find(i => i.name === name)
        if (hit) return hit
      }
      return null
    },
    onInstrumentPick(name, cell) {
      const inst = this.findInstrument(name)
      if (!inst) return
      const yqmcKey = cell && cell.fk ? this.resolveKey(cell.fk) : ''
      const siblings = instrumentSiblingKeys(yqmcKey)
      if (!siblings) return
      if (inst.code) this.$set(this.model, siblings.code, inst.code)
      if (inst.traceMethod) {
        this.$set(this.model, siblings.trace, inst.traceMethod)
        if (siblings.trace === this.traceFieldKey) {
          this.applyTraceMethodFromValue(inst.traceMethod)
        }
      }
      if (inst.traceExpiry) this.$set(this.model, siblings.expiry, inst.traceExpiry)
    },
    applyTraceMethodFromValue(val) {
      const v = String(val || '').trim()
      this.traceJd = v === '检定' || v === '检定/校准'
      this.traceJz = v === '校准' || v === '检定/校准'
    },
    syncTraceMethod() {
      this.syncingTrace = true
      let next = ''
      if (this.traceJd && this.traceJz) next = '检定/校准'
      else if (this.traceJd) next = '检定'
      else if (this.traceJz) next = '校准'
      if (this.traceFieldKey) this.$set(this.model, this.traceFieldKey, next)
      this.$nextTick(() => { this.syncingTrace = false })
    },
    onTraceMethodInput(val) {
      this.syncingTrace = true
      this.applyTraceMethodFromValue(val)
      this.$nextTick(() => { this.syncingTrace = false })
    }
  }
}
</script>

<style scoped>
.origin-form-wrap {
  overflow-x: auto;
  border: 1px solid #333;
  background: #fff;
}
.origin-form-wrap--join {
  border-top: none;
}
.origin-form {
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
}
.origin-form td {
  border: 1px solid #333;
  padding: 4px 6px;
  vertical-align: middle;
  word-break: break-word;
}
.origin-cell--label {
  background: #f7f7f7;
  text-align: center;
  font-weight: 600;
  color: #303133;
}
.origin-cell--vertical {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  letter-spacing: 2px;
  padding: 8px 4px;
}
.origin-cell--data {
  background: #fff;
}
.origin-inline-label {
  display: block;
  margin-bottom: 2px;
  font-weight: 600;
  color: #303133;
}
.origin-input >>> .el-input__inner,
.origin-input >>> .el-textarea__inner {
  border: none;
  border-bottom: 1px solid #dcdfe6;
  border-radius: 0;
  padding: 0 2px;
  background: transparent;
}
.origin-input >>> .el-input__inner {
  height: 26px;
}
.origin-select >>> .el-input__inner {
  padding-left: 4px;
  padding-right: 24px;
}
.origin-trace-wrap {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 6px;
  width: 100%;
}
.origin-trace-cb {
  margin-right: 4px;
}
.origin-select--inline {
  flex: 1;
  min-width: 72px;
}
.origin-readonly {
  color: #606266;
}
</style>
